import { HttpErrorResponse, HttpEvent, HttpInterceptorFn, HttpRequest } from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { catchError, finalize, from, mergeMap, Observable, throwError } from 'rxjs';
import { LoaderService } from '../service/loader.service';
import { TokenStorage } from './token-storage';
import { AuthService } from './auth.service';
import FingerprintJS from '@fingerprintjs/fingerprintjs';
import { environment } from 'src/environments/environment';
import { MatDialogComponent } from '../components/dialog-box/mat-dialog/mat-dialog.component';
import { of, Subject } from 'rxjs';
const fpPromise = FingerprintJS.load();
let loginInProgress = false;
let loginSubject: Subject<string | null> | null = null;
export const authInterceptor: HttpInterceptorFn = (req, next) => {
  const tokenStorage = inject(TokenStorage);
  const authService = inject(AuthService);
  const loader = inject(LoaderService);
  const dialog = inject(MatDialog);
  const router = inject(Router);

  // Check for token and fingerprint before sending request
  return from(getTokenAndFingerprint()).pipe(
    mergeMap(({ token, fingerprint }) => {
      req = cloneRequestWithAuthHeaders(req, token, fingerprint);

      if (!req.headers.get('skip-loader')) {
        loader.showLoader(); // only show loader if not skipped
      }  // Show loader during the request
      return next(req).pipe(
        finalize(() => loader.hideLoader()), // Hide loader after request
        catchError((error: HttpErrorResponse) => handleHttpError(error, req, next, authService, dialog, loader, router, tokenStorage))
      );
    })
  );
};

async function getTokenAndFingerprint(): Promise<{ token: string | null; fingerprint: string }> {
  const tokenStorage = inject(TokenStorage);
  const token = tokenStorage.get();

  const fp = await fpPromise;
  const fingerprintResult = await fp.get();
  const fingerprint = fingerprintResult.visitorId;

  return { token, fingerprint };
}

function cloneRequestWithAuthHeaders(req: HttpRequest<any>, token: string | null, fingerprint: string): HttpRequest<any> {
  return req.clone({
    setHeaders: {
      orgAppId: environment.orgAppId.toString(),
      fingerprint,
      ...(token ? { Authorization: `Bearer ${token}` } : {})
    }
  });
}

function handleHttpError(
  error: HttpErrorResponse,
  req: HttpRequest<any>,
  next: { (req: HttpRequest<unknown>): Observable<HttpEvent<unknown>> },
  authService: AuthService,
  dialog: MatDialog,
  loader: LoaderService,
  router: Router,
  tokenStorage: TokenStorage
): Observable<HttpEvent<any>> {
  if (error.status === 401) {
    return handleUnauthorizedError(req, next, authService, dialog, loader, router, tokenStorage);
  } else {
    return handleNonAuthError(error, dialog, router);
  }
}

function handleUnauthorizedError(
  req: HttpRequest<any>,
  next: { (req: HttpRequest<unknown>): Observable<HttpEvent<unknown>> },
  authService: AuthService,
  dialog: MatDialog,
  loader: LoaderService,
  router: Router,
  tokenStorage: TokenStorage
): Observable<HttpEvent<any>> {
  if (!loginInProgress) {
    loginInProgress = true;
    loginSubject = new Subject<string | null>();

    authService.BrowserLogin().subscribe({
      next: (response: any) => {
        loginInProgress = false;
        if (response?.rcode === 200) {
          const newToken = response.rObj.token;
          tokenStorage.save(newToken);
          loginSubject?.next(newToken);
          loginSubject?.complete();
        } else {
          loginSubject?.error('Browser login failed');
          showSessionExpiredPopup(dialog, loader, router);
        }
      },
      error: () => {
        loginInProgress = false;
        loginSubject?.error('Browser login failed');
        showSessionExpiredPopup(dialog, loader, router);
      }
    });
  }

  // Wait for login result
  return loginSubject!.pipe(
    mergeMap((newToken: string | null) => {
      return next(cloneRequestWithAuthHeaders(req, newToken, req.headers.get('fingerprint') || ''));
    }),
    catchError(() => {
      return throwError(() => new Error('Browser login failed'));
    })
  );
}


function handleNonAuthError(error: HttpErrorResponse, dialog: MatDialog, router: Router): Observable<HttpEvent<any>> {
  switch (error.status) {
    case 404:
      dialog.closeAll();
      router.navigate(['/landing']);
      break;
    case 403:
      dialog.closeAll();
      router.navigate(['/error/error-status-403']);
      break;
    case 500:
      dialog.closeAll();
      const transactionRef = error.error.transactionRef || '';
      router.navigate(['/error/error-status-500', transactionRef]);
      break;
    default:
      break;
  }
  return throwError(() => error);
}

function showSessionExpiredPopup(dialog: MatDialog, loader: LoaderService, router: Router): void {
  loader.hide();  // Hide loader when showing the popup
  const dialogRef = dialog.open(MatDialogComponent, {
    width: '50VW',
    disableClose: true,
    data: {
      tokenExpired: true,
      errorMessage: 'Your session has expired. Please log in again to continue.'
    }
  });

  dialogRef.afterClosed().subscribe(result => {
    if (result) {
      localStorage.clear();
      router.navigate(['/landing']).then(() => {
        loader.hide();
        window.location.reload(); // Reload the page after login
      });
    }
  });
}
